@import "../../common/base";
@import "../../common/icons";

@include input-reset();
@include button-reset(); // Should be placed before the box-sizing reset
@include box-sizing();

:host {
  /**
   * @prop --ch-combo-box-group__expandable-button-size:
   * Specifies the expandable button size in the group items.
   * @default #{$default-decorative-image-size}
   */
  --ch-combo-box-group__expandable-button-size: #{$default-decorative-image-size};

  /**
   * @prop --ch-combo-box-group__expandable-button-image-size:
   * Specifies the image size of the expandable button size in the group items. 
   * @default 100%
   */
  --ch-combo-box-group__expandable-button-image-size: #{$default-decorative-image-size};

  /**
   * @prop --ch-combo-box-item__image-size:
   * Specifies the box size that contains an image in the items. For example,
   * the box for the startImgSrc and endImgSrc
   * @default #{$default-decorative-image-size}
   */
  --ch-combo-box-item__image-size: #{$default-decorative-image-size};

  /**
   * @prop --ch-combo-box-item__background-image-size:
   * Specifies the image size of the items. For example, the image size for the
   * startImgSrc and endImgSrc
   * @default 100%
   */
  --ch-combo-box-item__background-image-size: 100%;

  /**
   * @prop --ch-combo-box__picker:
   * Specifies the image of the combo-box's picker.
   * @default url('data:image/svg+xml,<svg width="8" height="12" viewBox="0 0 8 12" xmlns="http://www.w3.org/2000/svg"><path d="M4.16669 0.666626L7.66669 4.66663H0.666687L4.16669 0.666626ZM4.16669 11.3333L0.666687 7.33329H7.66669L4.16669 11.3333Z"/></svg>')
   */
  --ch-combo-box__picker: url('data:image/svg+xml,<svg width="8" height="12" viewBox="0 0 8 12" xmlns="http://www.w3.org/2000/svg"><path d="M4.16669 0.666626L7.66669 4.66663H0.666687L4.16669 0.666626ZM4.16669 11.3333L0.666687 7.33329H7.66669L4.16669 11.3333Z"/></svg>');

  /**
   * @prop --ch-combo-box__picker-color:
   * Specifies the color of the combo-box's picker.
   * @default currentColor
   */
  --ch-combo-box__picker-color: currentColor;

  /**
   * @prop --ch-combo-box__picker-size:
   * Specifies the box size that contains the combo-box's picker.
   * @default #{$default-decorative-image-size}
   */
  --ch-combo-box__picker-size: #{$default-decorative-image-size};

  /**
   * @prop --ch-combo-box__picker-mask-size:
   * Specifies the image size of the combo-box's picker.
   * @default 100%
   */
  --ch-combo-box__picker-image-size: 100%;

  /**
   * @prop --ch-combo-box-item-gap:
   * Specifies the spacing between the images, text and the expandable button
   * on items.
   * @default 0px
   */
  --ch-combo-box-item-gap: 0px;

  /**
   * @prop --ch-combo-box-separation:
   * Specifies the separation between the combo-box and the displayed popover.
   * @default 0px
   */
  --ch-combo-box-separation: 0px;

  /**
   * @prop --ch-combo-box-separation-x:
   * Specifies the separation between the combo-box and the popover displayed
   * on the x-axis.
   * @default var(--ch-combo-box-separation)
   */
  --ch-combo-box-separation-x: var(--ch-combo-box-separation);

  /**
   * @prop --ch-combo-box-separation-y:
   * Specifies the separation between the combo-box and the popover displayed
   * on the y-axis.
   * @default var(--ch-combo-box-separation)
   */
  --ch-combo-box-separation-y: var(--ch-combo-box-separation);

  display: inline-grid;
  grid-template-columns: 1fr max-content;
  grid-template-rows: max-content 1fr;
  align-items: center;

  &::after {
    grid-row: 1 / 3;
    grid-column: 2;
    inline-size: var(--ch-combo-box__picker-size);
    block-size: var(--ch-combo-box__picker-size);
    -webkit-mask: var(--ch-combo-box__picker) 50% 50% /
      var(--ch-combo-box__picker-image-size)
      var(--ch-combo-box__picker-image-size) no-repeat;
    margin-inline-start: var(--ch-combo-box-item-gap);
    background-color: var(--ch-combo-box__picker-color);
    pointer-events: none;
  }
}

:host(.ch-combo-box--normal) {
  cursor: pointer;
  // Remove text selection on double click
  user-select: none;

  &::after {
    content: "";
  }
}

:host(.ch-combo-box--suggest) {
  --ch-combo-box__picker-size: 0px;
  cursor: text;
}

:host(.ch-disabled) {
  pointer-events: none;
}

.input-container {
  grid-row: 2 / 3;

  // Remove outline of the focus state.
  outline: unset;
}

.invisible-text {
  visibility: hidden;
  pointer-events: none;
  block-size: 0;

  // TODO: Add a unit test to verify that the input does not overflows the
  // combo-box when it has a reduced width (for example, 120px)
  overflow: hidden;
}

select {
  all: unset;
  display: grid;
  overflow: hidden;
  grid-row: 2 / 3;
}

.value {
  display: inline-grid;
  align-items: center;

  // TODO: Add a unit test to verify that the input does not overflows the
  // combo-box when it has a reduced width (for example, 120px)
  inline-size: 100%;

  &--readonly {
    pointer-events: none;
    text-overflow: ellipsis;
  }
}

// - - - - - - - - - - - - - - - -
//           Separation
// - - - - - - - - - - - - - - - -
ch-popover {
  --ch-popover-separation-x: var(--ch-combo-box-separation-x);
  --ch-popover-separation-y: var(--ch-combo-box-separation-y);

  // We must reset the cursor to avoid the inheriting "cursor: pointer" from
  // the Host
  cursor: auto;

  // --ch-popover-min-inline-size: var(--ch-popover-action-width);

  &:not(.hydrated) {
    opacity: 0;
  }
}

// - - - - - - - - - - - - - - - -
//         Leaf and Group
// - - - - - - - - - - - - - - - -
.leaf,
.group__header,
.group__header-caption,
.input-container {
  display: flex;
  gap: var(--ch-combo-box-item-gap);
  align-items: center;
  flex: 1; // Necessary to properly align the end image in expandable groups
}

.group {
  display: grid;
  grid-template-rows: max-content 1fr;

  &--expandable {
    &::before {
      content: "";
      inline-size: var(--ch-combo-box-group__expandable-button-size);
      block-size: var(--ch-combo-box-group__expandable-button-size);
      background-color: currentColor;
      -webkit-mask: $expandable-icon 50% 50% /
        var(--ch-combo-box-group__expandable-button-image-size)
        var(--ch-combo-box-group__expandable-button-image-size) no-repeat;
    }
  }

  &--collapsed::before {
    transform: rotate(-90deg); // TODO: Add RTL support
  }

  &__content {
    display: grid;
    grid-auto-rows: max-content;

    &--collapsed {
      display: none;
    }
  }
}

// - - - - - - - - - - - - - - - -
//             Images
// - - - - - - - - - - - - - - - -
.img--start::before,
.img--end::after {
  content: "";
  display: block;
  inline-size: var(--ch-combo-box-item__image-size);
  block-size: var(--ch-combo-box-item__image-size);
  min-inline-size: var(--ch-combo-box-item__image-size);
  pointer-events: none; // TODO: Add a unit test for this
}

.img--start {
  --ch-combo-box-item-start-img: var(--ch-start-img--base);

  // Leafs (role="option") only supports selected and disabled state.
  &.selected.start-img--selected {
    --ch-combo-box-item-start-img: var(--ch-start-img--selected);
  }

  // Group header
  .group__header:hover > &.start-img--hover {
    --ch-combo-box-item-start-img: var(--ch-start-img--hover);
  }

  .group__header:active > &.start-img--active {
    --ch-combo-box-item-start-img: var(--ch-start-img--active);
  }

  // Icon on the Host
  :host(:focus) &.input-container.start-img--focus {
    --ch-combo-box-item-start-img: var(--ch-start-img--focus);
  }

  :host(:not(.ch-disabled):hover) &.input-container.start-img--hover {
    --ch-combo-box-item-start-img: var(--ch-start-img--hover);
  }

  // Disabled
  &.disabled.start-img--disabled,
  .disabled > &.start-img--disabled,
  :host(.ch-disabled) &.start-img--disabled {
    --ch-combo-box-item-start-img: var(--ch-start-img--disabled);
  }
}

.img--end {
  --ch-combo-box-item-end-img: var(--ch-end-img--base);

  &::after {
    margin-inline-start: auto;
  }

  // Leafs (role="option") only supports selected and disabled state.
  &.selected.end-img--selected {
    --ch-combo-box-item-end-img: var(--ch-end-img--selected);
  }

  .group__header:hover > &.end-img--hover {
    --ch-combo-box-item-end-img: var(--ch-end-img--hover);
  }

  .group__header:active > &.end-img--active {
    --ch-combo-box-item-end-img: var(--ch-end-img--active);
  }

  &.disabled.end-img--disabled,
  .disabled > &.end-img--disabled {
    --ch-combo-box-item-end-img: var(--ch-end-img--disabled);
  }
}

.start-img-type--background::before {
  background: no-repeat center / var(--ch-combo-box-item__background-image-size)
    var(--ch-combo-box-item-start-img);
}

.end-img-type--background::after {
  background: no-repeat center / var(--ch-combo-box-item__background-image-size)
    var(--ch-combo-box-item-end-img);
}

.start-img-type--mask::before {
  -webkit-mask: no-repeat center /
    var(--ch-combo-box-item__background-image-size)
    var(--ch-combo-box-item-start-img);
  background-color: currentColor;
}

.end-img-type--mask::after {
  -webkit-mask: no-repeat center /
    var(--ch-combo-box-item__background-image-size)
    var(--ch-combo-box-item-end-img);
  background-color: currentColor;
}
